import { useState } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { Switch } from "./ui/Switch";

const NOTIFICATION_SOUNDS = [
  { id: "simple", name: "Simple", path: "/sounds/notifications/simple.mp3" },
  { id: "ring", name: "Ringing", path: "/sounds/notifications/ring.mp3" },
];

export function NotificationSettings() {
  const { unreadCount, preferences, updatePreferences } = useNotification();
  const [soundMenuOpen, setSoundMenuOpen] = useState(false);
  const [selectedSound, setSelectedSound] = useState(localStorage.getItem("snudbud_notification_sound") || "simple");

  // Play selected notification sound
  const playSound = (soundPath: string) => {
    const audio = new Audio(soundPath);
    audio.volume = 0.5;
    audio.play().catch((err) => {
      console.warn("Could not play notification sound:", err);
    });
  };

  // Play currently selected sound
  const playTestSound = () => {
    const sound = NOTIFICATION_SOUNDS.find((s) => s.id === selectedSound);
    if (sound) {
      playSound(sound.path);
    }
  };

  // Set the selected sound and update localStorage
  const selectSound = (soundId: string) => {
    setSelectedSound(soundId);
    localStorage.setItem("snudbud_notification_sound", soundId);

    // Also update the default notification sound path
    const soundPath = NOTIFICATION_SOUNDS.find((s) => s.id === soundId)?.path || "/sounds/notification.mp3";
    localStorage.setItem("snudbud_notification_sound_path", soundPath);

    // Close the dropdown
    setSoundMenuOpen(false);
  };

  // Get selected sound name
  const getSelectedSoundName = () => {
    return NOTIFICATION_SOUNDS.find((s) => s.id === selectedSound)?.name || "Simple Tone";
  };

  return (
    <div className="bg-white rounded-xl shadow p-4 md:p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Notification Settings</h2>

      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-700">Notification Sounds</p>
            <p className="text-sm text-gray-500">Play sounds when new messages arrive</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={preferences.enableSound}
              onCheckedChange={(checked) => updatePreferences({ enableSound: checked })}
            />
          </div>
        </div>

        {preferences.enableSound && (
          <div className="ml-4 bg-gray-50 p-3 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700">Sound:</p>
              <div className="relative">
                <button
                  onClick={() => setSoundMenuOpen(!soundMenuOpen)}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-1.5 text-sm w-40 text-left focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <span>{getSelectedSoundName()}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-500 transition-transform ${
                      soundMenuOpen ? "transform rotate-180" : ""
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {soundMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-100">
                    <ul className="py-1">
                      {NOTIFICATION_SOUNDS.map((sound) => (
                        <li key={sound.id}>
                          <button
                            onClick={() => selectSound(sound.id)}
                            className={`flex items-center justify-between w-full px-4 py-2 text-sm hover:bg-gray-50 ${
                              selectedSound === sound.id ? "text-primary font-medium" : "text-gray-700"
                            }`}
                          >
                            <span>{sound.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                playSound(sound.path);
                              }}
                              className="text-gray-400 hover:text-gray-700 p-1 rounded-full"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </button>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={playTestSound}
                className="bg-primary bg-opacity-10 text-primary px-3 py-1.5 rounded text-xs font-medium hover:bg-opacity-20 transition-colors flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.465a5 5 0 001.423 1.423"
                  />
                </svg>
                Test Sound
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-700">Browser Tab Notifications</p>
            <p className="text-sm text-gray-500">Show unread count in browser tab</p>
          </div>
          <Switch
            checked={preferences.enableTitleNotifications}
            onCheckedChange={(checked) => updatePreferences({ enableTitleNotifications: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-700">Browser Notifications</p>
            <p className="text-sm text-gray-500">Show desktop notifications when the tab is not active</p>
          </div>
          <Switch
            checked={preferences.enableBrowserNotifications}
            onCheckedChange={(checked) => updatePreferences({ enableBrowserNotifications: checked })}
          />
        </div>
      </div>

      {unreadCount > 0 && (
        <div className="mt-5 pt-5 border-t border-gray-100">
          <div className="bg-blue-50 text-blue-800 px-4 py-3 rounded-lg text-sm flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 text-blue-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              You have {unreadCount} unread message{unreadCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
